import { Home, BookOpen, TrendingUp, Cigarette, Brain, MessageCircle } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useProduct } from "@/hooks/useProduct";
import { useCrispUnreadCount } from "@/hooks/useCrispUnreadCount";

interface TabItem {
  key: string;
  icon: React.ElementType;
  labelKey: string;
  fallback: string;
  path: string;
}

export function BottomTabBar() {
  const { t } = useTranslation();
  const location = useLocation();
  const { config, productId } = useProduct();
  const pathPrefix = config.routing.pathPrefix;
  const unreadCount = useCrispUnreadCount();

  // Product-specific quick action tab
  const getQuickActionTab = (): TabItem => {
    if (productId === 'mindcotine') {
      return {
        key: "log",
        icon: Cigarette,
        labelKey: "common:nav.logCig",
        fallback: t('common.bottomtabbar.log', 'Log'),
        path: `${pathPrefix}/dashboard?action=log-cigarette`,
      };
    }
    // MindCo Relief - log stress
    return {
      key: "log",
      icon: Brain,
      labelKey: "common:nav.logStress",
      fallback: t('common.bottomtabbar.log', 'Log'),
      path: `${pathPrefix}/dashboard?action=log-stress`,
    };
  };

  const tabs: TabItem[] = [
    {
      key: "home",
      icon: Home,
      labelKey: "common:nav.home",
      fallback: t('common.bottomtabbar.home', 'Home'),
      path: `${pathPrefix}/dashboard`,
    },
    {
      key: "library",
      icon: BookOpen,
      labelKey: "common:nav.library",
      fallback: t('common.bottomtabbar.library', 'Library'),
      path: `${pathPrefix}/library`,
    },
    getQuickActionTab(),
    {
      key: "progress",
      icon: TrendingUp,
      labelKey: "common:nav.progress",
      fallback: t('common.bottomtabbar.progress', 'Progress'),
      path: `${pathPrefix}/tracking`,
    },
    {
      key: "chat",
      icon: MessageCircle,
      labelKey: "common:nav.chat",
      fallback: t('common.bottomtabbar.chat', 'Chat'),
      path: `${pathPrefix}/chat`,
    },
  ];

  const isActive = (tab: TabItem) => {
    const currentPath = location.pathname;

    // Home is active only on dashboard
    if (tab.key === "home") {
      return currentPath.includes("/dashboard");
    }

    // Log action is never "active" in the traditional sense
    if (tab.key === "log") {
      return false;
    }

    // Chat tab active check
    if (tab.key === "chat") {
      return currentPath.includes("/chat");
    }

    // For other tabs, check path match
    return currentPath.includes(tab.path);
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 lg:hidden">
      <div className="bg-background/95 backdrop-blur-md shadow-[0_-4px_12px_-2px_rgba(0,0,0,0.15),0_-2px_6px_-2px_rgba(0,0,0,0.1)] dark:shadow-[0_-4px_12px_-2px_rgba(0,0,0,0.4),0_-2px_6px_-2px_rgba(0,0,0,0.3)]">
        <div className="flex items-center justify-around h-16 px-2 pb-safe">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const active = isActive(tab);
            const showBadge = tab.key === "chat" && unreadCount > 0;
            const isLogButton = tab.key === "log";
            
            // Special FAB styling for Log button
            if (isLogButton) {
              return (
                <Link
                  key={tab.key}
                  to={tab.path}
                  className="flex items-center justify-center -mt-6 mx-1"
                >
                  <div className="flex flex-col items-center justify-center bg-primary text-primary-foreground rounded-full w-14 h-14 shadow-lg hover:scale-105 active:scale-95 transition-transform">
                    <Icon className="h-6 w-6" />
                  </div>
                </Link>
              );
            }
            
            return (
              <Link
                key={tab.key}
                to={tab.path}
                className={cn(
                  "flex flex-col items-center justify-center flex-1 h-full py-2 transition-colors relative",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-md",
                  active
                    ? "text-primary"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <div className="relative">
                  <Icon
                    className={cn(
                      "h-5 w-5 transition-transform",
                      active && "scale-110"
                    )}
                  />
                  {showBadge && (
                    <span className="absolute -top-1.5 -right-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </div>
                <span
                  className={cn(
                    "text-xs font-medium mt-1",
                    active && "font-semibold"
                  )}
                >
                  {t(tab.labelKey, tab.fallback)}
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
